import { t } from '../i18n'
import { initData } from './telegram'
import type {
  Answer,
  BackupFile,
  Checkin,
  Conflict,
  Connection,
  Day,
  ExportSummary,
  Flag,
  Photo,
  PhotoAngle,
  Product,
  ProductSummary,
  Profile,
  Reaction,
  Settings,
  Slot,
  Step,
  TimelineDay,
  Token,
} from './types'

export class ApiError extends Error {
  readonly status: number

  constructor(status: number, message: string) {
    super(message)
    this.status = status
    this.name = 'ApiError'
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const response = await fetch(path, {
    method,
    headers: {
      'X-Telegram-Init-Data': initData(),
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  })

  if (!response.ok) {
    throw new ApiError(response.status, await errorMessage(response))
  }
  if (response.status === 204) {
    return undefined as T
  }

  return (await response.json()) as T
}

async function blob(path: string): Promise<Blob> {
  const response = await fetch(path, {
    headers: { 'X-Telegram-Init-Data': initData() },
  })

  if (!response.ok) {
    throw new ApiError(response.status, await errorMessage(response))
  }

  return response.blob()
}

async function errorMessage(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as {
      detail?: string
      error?: string
      errors?: { message?: string; location?: string }[]
    }

    const first = body.errors?.[0]?.message
    if (first) return translate(first)

    if (response.status === 422 && body.detail) return body.detail

    return t.common.error(response.status)
  } catch {
    return t.common.error(response.status)
  }
}

function translate(message: string): string {
  const bound = /^expected number (<=|>=) (\d+)$/.exec(message)
  if (bound) {
    return bound[1] === '<=' ? t.errors.atMost(bound[2] ?? '') : t.errors.atLeast(bound[2] ?? '')
  }
  if (message === 'expected required property to be present') return t.errors.missingField
  if (message.startsWith('expected string to match')) return t.errors.badFormat
  return message
}

function query(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value))
  }
  const encoded = search.toString()
  return encoded ? `?${encoded}` : ''
}

const base = '/api/v1'

export const api = {
  day: (date: string) => request<{ day: Day }>('GET', `${base}/days/${date}`),

  mark: (
    date: string,
    mark: {
      slot: Slot
      productId: string
      done?: boolean
      skipped?: boolean
      skipReason?: string
      stung?: boolean
    },
  ) => request<{ day: Day }>('PUT', `${base}/days/${date}/marks`, mark),

  clearMark: (date: string, slot: Slot, productId: string) =>
    request<{ day: Day }>('DELETE', `${base}/days/${date}/marks${query({ slot, productId })}`),

  spf: (
    date: string,
    spf: { applied: boolean; reapplies?: number; exposure?: string; productId?: string },
  ) => request<{ day: Day }>('PUT', `${base}/days/${date}/spf`, spf),

  history: (from?: string, to?: string) =>
    request<{ days: TimelineDay[] }>('GET', `${base}/history${query({ from, to })}`),

  products: (
    filter: {
      search?: string
      status?: string
      category?: string
      active?: string
      slot?: Slot
    } = {},
  ) =>
    request<{ count: number; products: ProductSummary[] }>(
      'GET',
      `${base}/products${query(filter)}`,
    ),

  product: (id: string) => request<{ product: Product }>('GET', `${base}/products/${id}`),

  steps: (slot?: Slot) => request<{ steps: Step[] }>('GET', `${base}/steps${query({ slot })}`),

  conflicts: () => request<{ conflicts: Conflict[] }>('GET', `${base}/conflicts`),

  flags: () => request<{ flags: Flag[] }>('GET', `${base}/flags`),

  resolveFlag: (id: string) => request<void>('DELETE', `${base}/flags/${id}`),

  resolveConflict: (id: string) => request<void>('DELETE', `${base}/conflicts/${id}`),

  profile: () => request<{ profile: Profile }>('GET', `${base}/profile`),

  currentCheckin: (type: 'weekly' | 'monthly' = 'weekly') =>
    request<{ checkin: Checkin; answered: number; total: number }>(
      'GET',
      `${base}/checkins/current${query({ type })}`,
    ),

  answer: (id: string, answer: Answer) =>
    request<{ checkin: Checkin; answered: number; total: number }>(
      'PUT',
      `${base}/checkins/${id}/answers`,
      answer,
    ),

  submitCheckin: (id: string) =>
    request<{ checkin: Checkin; answered: number; total: number }>(
      'POST',
      `${base}/checkins/${id}/submit`,
    ),

  createReaction: (reaction: {
    symptoms: string[]
    severity: string
    zones?: string[]
    productId?: string
    actionTaken?: string
    occurredOn?: string
  }) => request<{ reaction: Reaction }>('POST', `${base}/reactions`, reaction),

  exportSummary: (from?: string, to?: string) =>
    request<{ summary: ExportSummary }>('GET', `${base}/export/summary${query({ from, to })}`),

  exportLink: (from?: string, to?: string) =>
    request<{ url: string; expiresIn: number }>('GET', `${base}/export/link${query({ from, to })}`),

  backupLink: () => request<{ url: string; expiresIn: number }>('GET', `${base}/backup/link`),

  restore: (backup: BackupFile) =>
    request<{ restored: Record<string, number> }>('POST', `${base}/backup/restore`, backup),

  connection: () => request<Connection>('GET', `${base}/mcp`),

  mintToken: (name?: string) =>
    request<{ token: Token; link: string }>('POST', `${base}/mcp/tokens`, { name }),

  mcpLink: () => request<{ link: string }>('GET', `${base}/mcp/link`),

  revokeToken: (id: string) => request<void>('DELETE', `${base}/mcp/tokens/${id}`),

  events: () =>
    request<{ events: { tool: string; status: string; createdAt: string; error?: string }[] }>(
      'GET',
      `${base}/mcp/events`,
    ),

  photos: (angle?: PhotoAngle) =>
    request<{ photos: Photo[]; angles: PhotoAngle[] }>('GET', `${base}/photos${query({ angle })}`),

  uploadPhoto: async (angle: PhotoAngle, file: Blob, takenAt?: string) => {
    const response = await fetch(`${base}/photos${query({ angle, takenAt })}`, {
      method: 'POST',
      headers: {
        'X-Telegram-Init-Data': initData(),
        'Content-Type': 'image/jpeg',
      },
      body: file,
    })
    if (!response.ok) throw new ApiError(response.status, await errorMessage(response))
    return (await response.json()) as { photo: Photo }
  },

  deletePhoto: (id: string) => request<void>('DELETE', `${base}/photos/${id}`),

  photoBlob: (id: string) => blob(`${base}/photos/${id}/file`),

  settings: () => request<Settings>('GET', `${base}/settings`),

  saveSettings: (settings: Settings) => request<Settings>('PUT', `${base}/settings`, settings),
}
